import React from 'react';
import {useLocation} from "react-router-dom";
import { useAsync } from 'react-async-hook';
import Linkify from 'react-linkify';
import { taskchecklistApi } from '../../../apis/taskchecklist/taskchecklistApi';
import { CreateTaskChecklistModal } from './CreateTaskChecklistModal';
import { UpdateTaskChecklistModal } from './UpdateTaskChecklistModal';
import { DeleteTaskChecklistModal } from './DeleteTaskChecklistModal';
import icon438 from '../../../assets/images/ROOM_TaskBoard_Re4_38.png';
import icon439 from '../../../assets/images/ROOM_TaskBoard_Re4_39.png';
import icon312 from '../../../assets/images/5_ROOM_TaskBoard_Re3_12.png';
import icon314 from '../../../assets/images/5_ROOM_TaskBoard_Re3_14.png';
import icon323 from '../../../assets/images/5_ROOM_TaskBoard_Re3_23.png';
import icon417 from '../../../assets/images/5_ROOM_TaskBoard_Re4_17.png';
import { TaskChecklist } from '../../../types/types';
import useLoading from '../../../hooks/useLoading';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface TaskChecklistIndexProps {
  taskId: number;
}

export const TaskChecklistIndex = (props: TaskChecklistIndexProps) => {
  const { taskId } = props;
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const [taskChecklists, setTaskChecklists] = React.useState<TaskChecklist[]>(
    []
  );
  const [taskChecklist, setTaskChecklist] = React.useState<
    TaskChecklist | undefined
  >();
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);

  const { execute: reloadChecklist } = useAsync(async () => {
    const fetch = async () => {
      await withLoading(async () => {
        try {
          const response = await taskchecklistApi.index(taskId, {});
          setTaskChecklists(response.taskCheckList);
        } catch(e) {
          await logError(
            location.pathname,
            'チェックリストの取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, [taskId]);

  const handleChange = async (checked: boolean, checklist: TaskChecklist) => {
    await taskchecklistApi.update(
      checklist.id,
      { endFlg: checked, taskCheckContent: checklist.content },
      ''
    );
    await reloadChecklist();
  };

  const onCreateSubmitted = async () => {
    await reloadChecklist();
    setShowCreateModal(false);
  };

  const handleUpdateClick = (c: TaskChecklist) => {
    setTaskChecklist(c);
    setShowUpdateModal(true);
  };

  const onUpdateSubmitted = async () => {
    await reloadChecklist();
    setShowUpdateModal(false);
  };

  const handleDeleteClick = (c: TaskChecklist) => {
    setTaskChecklist(c);
    setShowDeleteModal(true);
  };

  const onDeleteSubmitted = async () => {
    await reloadChecklist();
    setShowDeleteModal(false);
  };

  return (
    <>
      {showCreateModal && (
        <CreateTaskChecklistModal
          {...{
            taskId,
            onSubmitted: onCreateSubmitted,
          }}
        />
      )}
      {showUpdateModal && taskChecklist && (
        <UpdateTaskChecklistModal
          {...{ checklist: taskChecklist, onSubmitted: onUpdateSubmitted }}
        />
      )}
      {showDeleteModal && taskChecklist && (
        <DeleteTaskChecklistModal
          {...{ checklist: taskChecklist, onSubmitted: onDeleteSubmitted }}
        />
      )}
      {taskChecklists.length > 0 ? (
        <div className="flex items-start">
          <div className="text-xs font-semibold text-primary mr-8 flex-none flex items-center">
            <img src={icon417} alt="icon" />
            <div className="ml-2">チェックリスト</div>
          </div>
          <div className="flex-grow">
            <div className="px-2 pb-2 border-b border-primary flex justify-between">
              <p className="font-bold text-sm">チェックリスト（サブタスク）</p>
              <div className="flex justify-end w-48">
                <div className="flex-wrap flex items-center justify-start">
                  {taskChecklists.map((c, index) =>
                    taskChecklists.filter((checklist) => checklist.endFlg)
                      .length > index ? (
                      <img key={c.id} src={icon438} alt="icon" />
                    ) : (
                      <img key={c.id} src={icon439} alt="icon" />
                    )
                  )}
                </div>
              </div>
            </div>
            <div>
              {taskChecklists.length > 0 ? (
                taskChecklists.map((c) => (
                  <div key={c.id} className="p-2">
                    <div className="flex items-center">
                      <div className="flex-none w-6 flex items-center">
                        <input
                          type="checkbox"
                          checked={c.endFlg}
                          className="checkbox"
                          onChange={(e) => handleChange(e.target.checked, c)}
                        />
                      </div>
                      <div className="flex-grow">
                        <p className="checklist-content">
                          <Linkify
                            componentDecorator={(
                              decoratedHref,
                              decoratedText,
                              key
                            ) => (
                              <a
                                target="_blank"
                                href={decoratedHref}
                                key={key}
                                rel="noreferrer"
                              >
                                {decoratedText}
                              </a>
                            )}
                          >
                            {c.content}
                          </Linkify>
                        </p>
                      </div>
                      <div className="flex-none w-16 ml-2 space-x-2 flex justify-end">
                        <button
                          type="button"
                          className="outline-none focus:outline-none"
                          onClick={() => handleUpdateClick(c)}
                        >
                          <img src={icon312} alt="icon" />
                        </button>
                        <button
                          type="button"
                          className="outline-none focus:outline-none"
                          onClick={() => handleDeleteClick(c)}
                        >
                          <img src={icon314} alt="icon" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <></>
              )}
              <div className="border-b border-primary">
                <button
                  type="button"
                  className="p-2 text-xs text-primary font-semibold outline-none focus:outline-none flex items-center"
                  onClick={() => setShowCreateModal(true)}
                >
                  <img src={icon323} alt="icon" />
                  <div className="ml-4">チェックリスト追加</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center">
            <div className="text-xs font-semibold text-primary flex-none mr-8 flex items-center">
              <img src={icon417} alt="icon" />
              <div className="ml-2">チェックリスト</div>
            </div>
            <div className="flex-grow flex">
              <button
                type="button"
                className="btn-primary rounded-md text-sm"
                onClick={() => setShowCreateModal(true)}
              >
                チェックリストを追加
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
